import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { normalizeFormData } from '../types/repeatRules';
import type { Section } from '../types/formTypeStructure';
import type { OTPAuthToken } from '../hooks/useOTPHandler';

interface FormState {
  forms: Record<string, Record<string, any>>;
}

export const useFormStore = defineStore('formStore', () => {
  const forms = ref<FormState['forms']>({});
  
  // Stockage des tokens OTP en mémoire uniquement (pas de localStorage pour la sécurité)
  const otpTokens = ref<Record<string, OTPAuthToken>>({});

  // Charger les données depuis localStorage au démarrage
  const storageKey = 'multi-form-drafts';
  try {
    const storedData = localStorage.getItem(storageKey);
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      if (parsedData && parsedData.forms) {
        // Filtrer les formulaires qui ont été supprimés
        const filteredForms: Record<string, Record<string, any>> = {};
        
        Object.keys(parsedData.forms).forEach(formId => {
          const isFormCleared = localStorage.getItem('form-cleared-' + formId) === 'true';
          
          if (!isFormCleared) {
            // Vérifier si les données ne sont pas toutes vides
            const formData = parsedData.forms[formId];
            const hasValidData = Object.values(formData).some((val: any) => 
              val !== '' && val !== null && val !== undefined && 
              !(Array.isArray(val) && val.length === 0)
            );
            
            if (hasValidData) {
              filteredForms[formId] = formData;
            }
          } else {
            console.log('🚫 Formulaire ' + formId + ' exclu du chargement - marqué comme supprimé');
          }
        });
        
        forms.value = filteredForms;
        console.log('📥 Données filtrées chargées depuis localStorage:', forms.value);
      }
    }
  } catch (error) {
    console.warn('❌ Erreur lors du chargement depuis localStorage:', error);
  }

  // Fonction pour sauvegarder dans localStorage
  const saveToStorage = () => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({ forms: forms.value }));
    } catch (error) {
      console.warn('❌ Erreur lors de la sauvegarde dans localStorage:', error);
    }
  };

  function setFormValue(formId: string, field: string, value: any) {
    // Vérifier si le formulaire a été marqué comme supprimé
    const isFormCleared = localStorage.getItem('form-cleared-' + formId) === 'true';
    
    if (isFormCleared) {
      console.log('🚫 setFormValue bloqué - formulaire supprimé');
      return;
    }
    
    if (!forms.value[formId]) {
      forms.value[formId] = {};
    }
    forms.value[formId][field] = value;
    saveToStorage();
  }

  function setAllFormValues(formId: string, values: Record<string, any>) {
    // Vérifier si le formulaire a été marqué comme supprimé
    const isFormCleared = localStorage.getItem('form-cleared-' + formId) === 'true';
    
    if (isFormCleared) {
      console.log('🚫 setAllFormValues bloqué - formulaire supprimé');
      return;
    }
    
    forms.value[formId] = values;
    saveToStorage();
  }

  function getFormValues(formId: string): Record<string, any> {
    return forms.value[formId] || {};
  }

  function clearForm(formId: string) {
    console.log('🔍 clearForm appelé avec formId:', formId);
    console.log('🔍 État avant suppression:', forms.value);
    
    // Supprimer du store
    delete forms.value[formId];
    
    // Forcer la suppression du localStorage pour ce formulaire spécifique
    const storageKey = 'multi-form-drafts';
    try {
      const storedData = localStorage.getItem(storageKey);
      console.log('🔍 Données localStorage avant suppression:', storedData);
      
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        console.log('🔍 Données parsées avant suppression:', parsedData);
        
        if (parsedData && parsedData.forms && parsedData.forms[formId]) {
          console.log('🔍 Suppression de la clé:', formId, 'dans localStorage');
          delete parsedData.forms[formId];
          localStorage.setItem(storageKey, JSON.stringify(parsedData));
          console.log('🔍 Données localStorage après suppression:', localStorage.getItem(storageKey));
        } else {
          console.log('🔍 Aucune donnée trouvée pour formId:', formId);
        }
      } else {
        console.log('🔍 Aucune donnée dans localStorage');
      }
    } catch (error) {
      console.warn('❌ Erreur lors de la suppression du localStorage:', error);
    }
    
    // Forcer la suppression en vidant complètement le localStorage si c'est le seul formulaire
    if (Object.keys(forms.value).length === 0) {
      console.log('🔍 Vider complètement le localStorage car plus de formulaires');
      localStorage.removeItem(storageKey);
    }
    
    console.log('🔍 État après suppression:', forms.value);
  }

  function clearAll() {
    forms.value = {};
    // Forcer la suppression complète du localStorage
    localStorage.removeItem('multi-form-drafts');
    console.log('🗑️ Store et localStorage vidés');
  }

  // Fonction pour forcer la suppression complète
  function forceClearForm(formId: string) {
    console.log('💥 forceClearForm appelé avec formId:', formId);
    
    // 1. Supprimer du store
    delete forms.value[formId];
    
    // 2. Vider complètement le localStorage
    localStorage.removeItem('multi-form-drafts');
    
    // 3. Forcer la synchronisation
    setTimeout(() => {
      const remainingData = getFormValues(formId);
      console.log('💥 Données restantes après forceClearForm:', remainingData);
    }, 50);
  }

  // Fonction pour désactiver temporairement la persistance
  function disablePersistence() {
    console.log('🔒 Désactivation de la persistance');
    // Vider le store pour éviter la restauration
    forms.value = {};
  }

  // Fonction pour réactiver la persistance
  function enablePersistence() {
    console.log('🔓 Réactivation de la persistance');
    // Recharger depuis le localStorage si nécessaire
    const storageKey = 'multi-form-drafts';
    try {
      const storedData = localStorage.getItem(storageKey);
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        if (parsedData && parsedData.forms) {
          // Ne restaurer que si les données ne sont pas vides
          const hasValidData = Object.values(parsedData.forms).some((formData: any) => 
            Object.values(formData).some((val: any) => 
              val !== '' && val !== null && val !== undefined && 
              !(Array.isArray(val) && val.length === 0)
            )
          );
          
          if (hasValidData) {
            forms.value = parsedData.forms;
            console.log('🔓 Données restaurées depuis localStorage');
          } else {
            console.log('🔓 Aucune donnée valide à restaurer');
          }
        }
      } else {
        console.log('🔓 Aucune donnée dans localStorage à restaurer');
      }
    } catch (error) {
      console.warn('❌ Erreur lors de la réactivation de la persistance:', error);
    }
  }

  // Fonction pour forcer la synchronisation avec des valeurs vides
  function forceEmptyForm(formId: string, emptyValues: Record<string, any>) {
    console.log('🔄 ForceEmptyForm appelé pour:', formId);
    
    // Vérifier si le formulaire a été marqué comme supprimé
    const isFormCleared = localStorage.getItem('form-cleared-' + formId) === 'true';
    
    if (isFormCleared) {
      // Si le formulaire a été supprimé, ne pas sauvegarder les valeurs vides
      console.log('🚫 Sauvegarde bloquée - formulaire supprimé');
      forms.value[formId] = emptyValues;
      // Ne pas appeler saveToStorage() pour éviter de sauvegarder les valeurs vides
    } else {
      forms.value[formId] = emptyValues;
      saveToStorage();
      console.log('🔄 Store mis à jour avec valeurs vides et sauvegardé');
    }
  }

  // Fonctions pour gérer les tokens OTP (stockage en mémoire uniquement)
  function setOTPToken(key: string, token: OTPAuthToken): void {
    otpTokens.value[key] = token;
    console.log('✅ [FormStore] Token OTP stocké en mémoire:', { key, tokenKeys: Object.keys(token) });
  }

  function getOTPToken(key: string): OTPAuthToken | null {
    const token = otpTokens.value[key];
    if (!token) return null;
    
    // Vérifier si le token est expiré
    if (token.expires_at && token.expires_at <= Date.now()) {
      console.warn('⚠️ [FormStore] Token OTP expiré, suppression:', key);
      delete otpTokens.value[key];
      return null;
    }
    
    return token;
  }

  function clearOTPToken(key: string): void {
    if (otpTokens.value[key]) {
      delete otpTokens.value[key];
      console.log('🧹 [FormStore] Token OTP supprimé:', key);
    }
  }

  function clearAllOTPTokens(): void {
    otpTokens.value = {};
    console.log('🧹 [FormStore] Tous les tokens OTP supprimés');
  }

  function hasOTPToken(key: string): boolean {
    return !!otpTokens.value[key];
  }

  return {
    forms,
    setFormValue,
    setAllFormValues,
    getFormValues,
    clearForm,
    clearAll,
    forceClearForm,
    disablePersistence,
    enablePersistence,
    forceEmptyForm,
    // Fonctions pour les tokens OTP
    setOTPToken,
    getOTPToken,
    clearOTPToken,
    clearAllOTPTokens,
    hasOTPToken,
    otpTokens: computed(() => otpTokens.value),
  };
});


export function useFormPersistence(formId: string, sections: Section[]) {
  const formStore = useFormStore();

  const savedValues = formStore.getFormValues(formId);

  function syncFormData(watchedValues: Record<string, any>) {
    const normalized = normalizeFormData(watchedValues, sections);
    formStore.setAllFormValues(formId, normalized);
  }

  return {
    savedValues,
    syncFormData,
  };
}